import { useAuth } from "@clerk/clerk-react";
import type { ResolvedEntitlements } from "@giftforward/entitlements";
import { useQuery } from "@tanstack/react-query";
import { fetchOrgEntitlements } from "@/api/entitlements";

/**
 * One organization's resolved entitlements.
 *
 * Pass the organization whose plan governs the thing on screen — the campaign's owner, the org a
 * GFG panel is looking at. For the caller's own, use `useMyEntitlements()`.
 *
 * Three deliberate differences from `usePermissions`:
 *
 * - **Bounded staleness.** Permissions cache forever because a role change implies a re-login. A
 *   plan moves on a Stripe webhook with no user action at all, so this refetches on focus.
 * - **Keyed on the organization**, not the user.
 * - **No fallback.** `useTier` returned "starter" while loading AND on error, so every gate
 *   painted Starter and flipped, and a 403 looked like a downgrade. Here `ent` is undefined until
 *   it resolves and callers wait on `isLoaded`.
 */
export function useOrgEntitlements(organizationId: number | null | undefined) {
  const { isSignedIn } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["entitlements", organizationId],
    queryFn: () => fetchOrgEntitlements(organizationId as number),
    enabled: !!isSignedIn && organizationId != null,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const ent: ResolvedEntitlements | undefined = data
    ? { plan: data.plan, entitlements: data.entitlements }
    : undefined;

  return { ent, isLoaded: !!ent, isLoading, isError };
}
